import React, { useEffect, useRef, useState } from 'react'
import CreatableSelect from 'react-select/creatable'
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react'
import { AdminService } from '../../services/adminService'
import {
  buildNextModelOutputReview,
  buildReviewCorrectionOption,
  DEFAULT_SKIN_CORRECTION_OPTIONS,
  getCorrectionOptionForReview,
  getFieldReview,
  getReviewSaveBadge,
  getSlotGroupAccentClasses,
  getSlotReview,
  mergeCorrectionOptions,
  normalizeModelOutputReview,
  normalizeSkinRoutineOutput,
  REVIEW_REASON_OPTIONS,
  SKIN_REVIEW_SLOT_GROUPS,
} from '../../utils/modelReviewUtils'

const getSaveStateBadgeClasses = (tone) => {
  if (tone === 'amber') {
    return 'bg-amber-100 text-amber-800 border border-amber-200'
  }

  if (tone === 'rose') {
    return 'bg-rose-100 text-rose-800 border border-rose-200'
  }

  if (tone === 'emerald') {
    return 'bg-emerald-100 text-emerald-800 border border-emerald-200'
  }

  return 'bg-slate-100 text-slate-700 border border-slate-200'
}

const getPerSlotStatusText = (status) => {
  if (status === 'saving') {
    return 'Saving changes...'
  }

  if (status === 'saved') {
    return 'Saved'
  }

  if (status === 'error') {
    return 'Could not save'
  }

  return 'Not reviewed yet'
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 12,
    borderColor: state.isFocused ? '#d946ef' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(217, 70, 239, 0.12)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#d946ef' : '#cbd5e1',
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 60,
    borderRadius: 14,
    overflow: 'hidden',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.data?.isCustomAction
      ? state.isFocused
        ? '#f5f3ff'
        : '#faf5ff'
      : state.isFocused
        ? '#fdf2f8'
        : '#ffffff',
    color: state.data?.isCustomAction ? '#7c3aed' : '#111827',
    fontWeight: state.data?.isCustomAction ? 700 : 500,
    cursor: 'pointer',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#94a3b8',
  }),
}

const CUSTOM_CORRECTION_OPTION = {
  value: '__custom_correction__',
  normalized_key: '__custom_correction__',
  label: 'Custom / Add new correction...',
  isCustomAction: true,
}

const JsonDrawer = ({ title, payload }) => (
  <details className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
    <summary className="cursor-pointer text-sm font-semibold text-slate-700">{title}</summary>
    <pre className="mt-3 overflow-x-auto whitespace-pre-wrap text-xs leading-6 text-slate-600">
      {payload || 'No JSON available.'}
    </pre>
  </details>
)

const SummaryComparisonRow = ({
  title,
  targetValue,
  modelValue,
  review,
  saveState,
  canEditReview,
  accentClass,
  correctionDraft,
  onVoteChange,
  onCorrectionChange,
  onCorrectionBlur,
  targetPaneClass = '',
  modelPaneClass = '',
}) => (
  <div className={`overflow-hidden rounded-[24px] border shadow-[0_12px_40px_rgba(15,23,42,0.06)] ${accentClass}`}>
    <div className="grid gap-px bg-slate-100 lg:grid-cols-2">
      <div className={`px-5 py-5 sm:px-6 ${targetPaneClass}`}>
        <div className="mb-2 flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            {title}
          </span>
          <span className="rounded-full bg-white/75 px-2 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-white/70">
            Target
          </span>
        </div>
        <p className="text-sm leading-7 text-slate-800">{targetValue || 'Not available.'}</p>
      </div>

      <div className={`px-5 py-5 sm:px-6 ${modelPaneClass}`}>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-fuchsia-700 shadow-sm ring-1 ring-white/70">
                Model
              </span>
              <span className="text-xs text-slate-400">{getPerSlotStatusText(saveState)}</span>
            </div>
            <p className="text-sm leading-7 text-slate-800">{modelValue || 'Not available.'}</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!canEditReview}
              onClick={() => onVoteChange('up')}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold transition ${
                review.vote === 'up'
                  ? 'border-emerald-500 bg-emerald-500 text-white shadow-sm'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-300 hover:text-emerald-700'
              } ${!canEditReview ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <ThumbsUp className="h-4 w-4" />
              Up
            </button>
            <button
              type="button"
              disabled={!canEditReview}
              onClick={() => onVoteChange('down')}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold transition ${
                review.vote === 'down'
                  ? 'border-rose-500 bg-rose-500 text-white shadow-sm'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-rose-300 hover:text-rose-700'
              } ${!canEditReview ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <ThumbsDown className="h-4 w-4" />
              Down
            </button>
          </div>
        </div>

        {review.vote === 'down' && (
          <div className="rounded-2xl border border-white/70 bg-white/55 px-4 py-4 backdrop-blur-sm">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Correction
            </label>
            <textarea
              value={correctionDraft}
              disabled={!canEditReview}
              onChange={(event) => onCorrectionChange(event.target.value)}
              onBlur={onCorrectionBlur}
              rows={title === 'Analysis' ? 4 : 2}
              placeholder={title === 'Analysis' ? 'Write the corrected analysis here...' : 'Write the corrected category text here...'}
              className={`w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-fuchsia-400 focus:ring-4 focus:ring-fuchsia-100 ${!canEditReview ? 'cursor-not-allowed opacity-60' : ''}`}
            />
          </div>
        )}
      </div>
    </div>
  </div>
)

const SkinModelReviewModal = ({
  isOpen,
  consultation,
  onClose,
  userRole,
  onReviewSaved,
}) => {
  const currentAdmin = AdminService.getCurrentUser()
  const [reviewState, setReviewState] = useState(() => normalizeModelOutputReview(null))
  const reviewStateRef = useRef(reviewState)
  const [otherReasonDrafts, setOtherReasonDrafts] = useState({})
  const [fieldCorrectionDrafts, setFieldCorrectionDrafts] = useState({})
  const [customCorrectionDrafts, setCustomCorrectionDrafts] = useState({})
  const [customCorrectionSlots, setCustomCorrectionSlots] = useState({})
  const [correctionOptions, setCorrectionOptions] = useState(() =>
    mergeCorrectionOptions(DEFAULT_SKIN_CORRECTION_OPTIONS)
  )
  const [slotSaveStates, setSlotSaveStates] = useState({})
  const [catalogError, setCatalogError] = useState('')
  const requestSequenceRef = useRef({})
  const otherReasonTimerRef = useRef({})
  const fieldCorrectionTimerRef = useRef({})

  useEffect(() => {
    reviewStateRef.current = reviewState
  }, [reviewState])

  useEffect(() => {
    return () => {
      Object.values(otherReasonTimerRef.current).forEach((timeoutId) => clearTimeout(timeoutId))
      otherReasonTimerRef.current = {}
      Object.values(fieldCorrectionTimerRef.current).forEach((timeoutId) => clearTimeout(timeoutId))
      fieldCorrectionTimerRef.current = {}
    }
  }, [])

  useEffect(() => {
    if (!isOpen || !consultation?.consultation_id) {
      return
    }

    Object.values(otherReasonTimerRef.current).forEach((timeoutId) => clearTimeout(timeoutId))
    otherReasonTimerRef.current = {}
    Object.values(fieldCorrectionTimerRef.current).forEach((timeoutId) => clearTimeout(timeoutId))
    fieldCorrectionTimerRef.current = {}

    const normalizedReview = normalizeModelOutputReview(consultation.model_output_review)
    setReviewState(normalizedReview)
    reviewStateRef.current = normalizedReview
    setOtherReasonDrafts(
      Object.fromEntries(
        Object.entries(normalizedReview.slots).map(([slotKey, slotReview]) => [
          slotKey,
          slotReview.other_reason_text || '',
        ])
      )
    )
    setFieldCorrectionDrafts(
      Object.fromEntries(
        Object.entries(normalizedReview.fields).map(([fieldKey, fieldReview]) => [
          fieldKey,
          fieldReview.correction_text || '',
        ])
      )
    )
    setCustomCorrectionDrafts({})
    setCustomCorrectionSlots({})
    setSlotSaveStates({})
    setCatalogError('')
    setCorrectionOptions(mergeCorrectionOptions(DEFAULT_SKIN_CORRECTION_OPTIONS))

    let isCancelled = false
    const fetchOptions = async () => {
      const result = await AdminService.getConsultationModelCorrectionOptions('skin')

      if (isCancelled) {
        return
      }

      if (result.success) {
        setCorrectionOptions((currentOptions) =>
          mergeCorrectionOptions(currentOptions, result.data || [])
        )
        return
      }

      setCatalogError(result.error || 'Could not load shared correction options.')
    }

    fetchOptions()

    return () => {
      isCancelled = true
    }
  }, [consultation?.consultation_id, consultation?.model_output_review, isOpen])

  if (!isOpen || !consultation) {
    return null
  }

  const canEditReview = userRole === 'super_admin'

  const targetOutput = normalizeSkinRoutineOutput(consultation.derma_output)
  const modelOutput = normalizeSkinRoutineOutput(consultation.model_output_json)
  const saveBadge = getReviewSaveBadge(slotSaveStates)

  const persistReviewEntry = async (reviewKey, reviewPatch) => {
    const previousReview = reviewStateRef.current
    const nextReview = buildNextModelOutputReview(previousReview, reviewKey, reviewPatch, {
      updatedAt: new Date().toISOString(),
      updatedBy: currentAdmin?.id || null,
    })

    const nextSequence = (requestSequenceRef.current[reviewKey] || 0) + 1
    requestSequenceRef.current[reviewKey] = nextSequence

    setReviewState(nextReview)
    reviewStateRef.current = nextReview
    setSlotSaveStates((currentStates) => ({
      ...currentStates,
      [reviewKey]: 'saving',
    }))

    const result = await AdminService.saveConsultationModelReviewSlot(
      consultation.consultation_id,
      reviewKey,
      reviewPatch,
      previousReview,
      {
        updatedBy: currentAdmin?.id || null,
      }
    )

    if (requestSequenceRef.current[reviewKey] !== nextSequence) {
      return
    }

    if (!result.success) {
      setReviewState(previousReview)
      reviewStateRef.current = previousReview
      setSlotSaveStates((currentStates) => ({
        ...currentStates,
        [reviewKey]: 'error',
      }))
      setCatalogError(result.error || 'Could not save this review change.')
      return
    }

    const normalizedReview = normalizeModelOutputReview(result.data || nextReview)
    setReviewState(normalizedReview)
    reviewStateRef.current = normalizedReview
    setSlotSaveStates((currentStates) => ({
      ...currentStates,
      [reviewKey]: 'saved',
    }))
    setCatalogError('')

    if (Object.prototype.hasOwnProperty.call(reviewPatch, 'other_reason_text')) {
      setOtherReasonDrafts((currentDrafts) => ({
        ...currentDrafts,
        [reviewKey]: normalizedReview.slots?.[reviewKey]?.other_reason_text || '',
      }))
    }

    if (Object.prototype.hasOwnProperty.call(reviewPatch, 'correction_text')) {
      setFieldCorrectionDrafts((currentDrafts) => ({
        ...currentDrafts,
        [reviewKey]: normalizedReview.fields?.[reviewKey]?.correction_text || '',
      }))
    }

    if (onReviewSaved) {
      onReviewSaved(consultation.consultation_id, normalizedReview)
    }
  }

  const handleVoteChange = (slotKey, vote) => {
    const currentSlotReview = getSlotReview(reviewState, slotKey)
    persistReviewEntry(slotKey, {
      ...currentSlotReview,
      vote,
    })
  }

  const handleFieldVoteChange = (fieldKey, vote) => {
    const currentFieldReview = getFieldReview(reviewState, fieldKey)
    persistReviewEntry(fieldKey, {
      ...currentFieldReview,
      vote,
    })
  }

  const handleCorrectionChange = (slotKey, option) => {
    if (option?.isCustomAction) {
      setCustomCorrectionSlots((currentSlots) => ({
        ...currentSlots,
        [slotKey]: true,
      }))
      return
    }

    setCustomCorrectionSlots((currentSlots) => ({
      ...currentSlots,
      [slotKey]: false,
    }))
    const currentSlotReview = getSlotReview(reviewState, slotKey)
    persistReviewEntry(slotKey, {
      ...currentSlotReview,
      correction_option_id: option?.id || null,
      correction_label: option?.label || null,
    })
  }

  const handleReasonToggle = (slotKey, reasonValue) => {
    const currentSlotReview = getSlotReview(reviewState, slotKey)
    const nextReasonTags = currentSlotReview.reason_tags.includes(reasonValue)
      ? currentSlotReview.reason_tags.filter((value) => value !== reasonValue)
      : [...currentSlotReview.reason_tags, reasonValue]

    persistReviewEntry(slotKey, {
      ...currentSlotReview,
      reason_tags: nextReasonTags,
    })
  }

  const clearOtherReasonTimer = (slotKey) => {
    const timeoutId = otherReasonTimerRef.current[slotKey]
    if (!timeoutId) {
      return
    }

    clearTimeout(timeoutId)
    delete otherReasonTimerRef.current[slotKey]
  }

  const persistOtherReasonText = (slotKey, value) => {
    const currentSlotReview = getSlotReview(reviewStateRef.current, slotKey)
    persistReviewEntry(slotKey, {
      ...currentSlotReview,
      other_reason_text: String(value || '').trim() || null,
    })
  }

  const clearFieldCorrectionTimer = (fieldKey) => {
    const timeoutId = fieldCorrectionTimerRef.current[fieldKey]
    if (!timeoutId) {
      return
    }

    clearTimeout(timeoutId)
    delete fieldCorrectionTimerRef.current[fieldKey]
  }

  const persistFieldCorrectionText = (fieldKey, value) => {
    const currentFieldReview = getFieldReview(reviewStateRef.current, fieldKey)
    persistReviewEntry(fieldKey, {
      ...currentFieldReview,
      correction_text: String(value || '').trim() || null,
    })
  }

  const handleFieldCorrectionChange = (fieldKey, value) => {
    setFieldCorrectionDrafts((currentDrafts) => ({
      ...currentDrafts,
      [fieldKey]: value,
    }))

    clearFieldCorrectionTimer(fieldKey)
    fieldCorrectionTimerRef.current[fieldKey] = setTimeout(() => {
      persistFieldCorrectionText(fieldKey, value)
      delete fieldCorrectionTimerRef.current[fieldKey]
    }, 450)
  }

  const handleFieldCorrectionBlur = (fieldKey) => {
    const trimmedValue = String(fieldCorrectionDrafts[fieldKey] || '').trim()
    clearFieldCorrectionTimer(fieldKey)
    setFieldCorrectionDrafts((currentDrafts) => ({
      ...currentDrafts,
      [fieldKey]: trimmedValue,
    }))
    persistFieldCorrectionText(fieldKey, trimmedValue)
  }

  const handleOtherReasonChange = (slotKey, value) => {
    setOtherReasonDrafts((currentDrafts) => ({
      ...currentDrafts,
      [slotKey]: value,
    }))

    clearOtherReasonTimer(slotKey)
    otherReasonTimerRef.current[slotKey] = setTimeout(() => {
      persistOtherReasonText(slotKey, value)
      delete otherReasonTimerRef.current[slotKey]
    }, 450)
  }

  const handleOtherReasonBlur = (slotKey) => {
    const trimmedValue = String(otherReasonDrafts[slotKey] || '').trim()
    clearOtherReasonTimer(slotKey)
    setOtherReasonDrafts((currentDrafts) => ({
      ...currentDrafts,
      [slotKey]: trimmedValue,
    }))
    persistOtherReasonText(slotKey, trimmedValue)
  }

  const handleCreateOption = async (slotKey, label) => {
    const createdLabel = String(label || '').trim()
    if (!createdLabel) {
      return null
    }

    const optimisticOption = buildReviewCorrectionOption(createdLabel)
    setCorrectionOptions((currentOptions) =>
      mergeCorrectionOptions(currentOptions, [optimisticOption])
    )

    const result = await AdminService.createConsultationModelCorrectionOption({
      label: createdLabel,
      scope: 'skin',
    })

    const optionToUse = result.success
      ? buildReviewCorrectionOption(result.data)
      : optimisticOption

    if (result.success) {
      setCorrectionOptions((currentOptions) =>
        mergeCorrectionOptions(currentOptions, [optionToUse])
      )
      setCatalogError('')
    } else {
      setCatalogError(result.error || 'Could not save this option globally. It will stay on this review only.')
    }

    handleCorrectionChange(slotKey, optionToUse)
    return optionToUse
  }

  const handleCustomCorrectionDraftChange = (slotKey, value) => {
    setCustomCorrectionDrafts((currentDrafts) => ({
      ...currentDrafts,
      [slotKey]: value,
    }))
  }

  const handleCustomCorrectionSubmit = async (slotKey) => {
    const draftValue = String(customCorrectionDrafts[slotKey] || '').trim()
    if (!draftValue) {
      return
    }

    const createdOption = await handleCreateOption(slotKey, draftValue)
    if (!createdOption) {
      return
    }

    setCustomCorrectionDrafts((currentDrafts) => ({
      ...currentDrafts,
      [slotKey]: '',
    }))
    setCustomCorrectionSlots((currentSlots) => ({
      ...currentSlots,
      [slotKey]: false,
    }))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-3 sm:p-5">
      <div className="flex max-h-[92vh] w-full max-w-[96rem] flex-col overflow-hidden rounded-[30px] border border-white/60 bg-white shadow-[0_30px_120px_rgba(15,23,42,0.25)]">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur sm:px-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-fuchsia-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-fuchsia-700">
                  Skin Review Workspace
                </span>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getSaveStateBadgeClasses(saveBadge.tone)}`}>
                  {saveBadge.label}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Target vs Model Output</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {consultation.name || 'Anonymous'} · {consultation.phone || 'No phone'} · Consultation ID {consultation.consultation_id}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700"
              aria-label="Close model review modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-4 py-5 sm:px-7">
          <div className="space-y-6">
            <div className="space-y-4">
              <SummaryComparisonRow
                title="Categories"
                targetValue={targetOutput.categories}
                modelValue={modelOutput.categories}
                review={getFieldReview(reviewState, 'model_categories')}
                saveState={slotSaveStates.model_categories}
                canEditReview={canEditReview}
                accentClass="border-sky-200 bg-gradient-to-br from-sky-100 via-cyan-50 to-white"
                targetPaneClass="bg-gradient-to-br from-sky-100 via-cyan-50 to-white"
                modelPaneClass="bg-gradient-to-br from-fuchsia-100 via-pink-50 to-white"
                correctionDraft={fieldCorrectionDrafts.model_categories || ''}
                onVoteChange={(vote) => handleFieldVoteChange('model_categories', vote)}
                onCorrectionChange={(value) => handleFieldCorrectionChange('model_categories', value)}
                onCorrectionBlur={() => handleFieldCorrectionBlur('model_categories')}
              />
              <SummaryComparisonRow
                title="Analysis"
                targetValue={targetOutput.analysis}
                modelValue={modelOutput.analysis}
                review={getFieldReview(reviewState, 'model_analysis')}
                saveState={slotSaveStates.model_analysis}
                canEditReview={canEditReview}
                accentClass="border-rose-200 bg-gradient-to-br from-rose-100 via-fuchsia-50 to-white"
                targetPaneClass="bg-gradient-to-br from-indigo-50 via-sky-50 to-white"
                modelPaneClass="bg-gradient-to-br from-rose-100 via-pink-50 to-white"
                correctionDraft={fieldCorrectionDrafts.model_analysis || ''}
                onVoteChange={(vote) => handleFieldVoteChange('model_analysis', vote)}
                onCorrectionChange={(value) => handleFieldCorrectionChange('model_analysis', value)}
                onCorrectionBlur={() => handleFieldCorrectionBlur('model_analysis')}
              />
            </div>

            {catalogError && (
              <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{catalogError}</span>
              </div>
            )}

            <div className="space-y-5">
              {SKIN_REVIEW_SLOT_GROUPS.map((group) => {
                const accentClasses = getSlotGroupAccentClasses(group.accent)

                return (
                  <section
                    key={group.key}
                    className={`rounded-[24px] border p-4 sm:p-5 ${accentClasses.panel}`}
                  >
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      <div className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${accentClasses.chip}`}>
                        {group.label}
                      </div>
                      <p className="text-sm text-slate-500">
                        Review each model suggestion slot by slot and keep corrections training-ready.
                      </p>
                    </div>

                    <div className="space-y-3">
                      {group.slots.map((slot) => {
                        const targetSlot = targetOutput.slots[slot.key]
                        const modelSlot = modelOutput.slots[slot.key]
                        const slotReview = getSlotReview(reviewState, slot.key)
                        const selectedCorrectionOption = getCorrectionOptionForReview(
                          correctionOptions,
                          slotReview
                        )
                        const slotSaveState = slotSaveStates[slot.key]
                        const otherReasonDraft =
                          otherReasonDrafts[slot.key] ?? slotReview.other_reason_text ?? ''
                        const isOtherReasonSelected = slotReview.reason_tags.includes('other')
                        const isCustomCorrectionOpen = Boolean(customCorrectionSlots[slot.key])
                        const customCorrectionDraft = customCorrectionDrafts[slot.key] || ''
                        const correctionSelectOptions = [
                          CUSTOM_CORRECTION_OPTION,
                          ...correctionOptions,
                        ]

                        return (
                          <div
                            key={slot.key}
                            className="overflow-hidden rounded-[22px] border border-white/70 bg-white/90 shadow-sm"
                          >
                            <div className="grid gap-px bg-slate-100 lg:grid-cols-2">
                              <div className="bg-white px-4 py-4 sm:px-5">
                                <div className="mb-2 flex items-center gap-2">
                                  <span className={`text-xs font-semibold uppercase tracking-[0.18em] ${accentClasses.slotLabel}`}>
                                    {slot.slotName}
                                  </span>
                                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                                    Target
                                  </span>
                                </div>
                                <p className="text-sm leading-7 text-slate-800">
                                  {targetSlot?.searchQuery || 'No target query available for this slot.'}
                                </p>
                              </div>

                              <div className="bg-white px-4 py-4 sm:px-5">
                                <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-fuchsia-700">
                                        Model
                                      </span>
                                      <span className="text-xs text-slate-400">{getPerSlotStatusText(slotSaveState)}</span>
                                    </div>
                                    <p className="text-sm leading-7 text-slate-800">
                                      {modelSlot?.searchQuery || 'No model suggestion stored for this slot yet.'}
                                    </p>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      disabled={!canEditReview}
                                      onClick={() => handleVoteChange(slot.key, 'up')}
                                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold transition ${
                                        slotReview.vote === 'up'
                                          ? 'border-emerald-500 bg-emerald-500 text-white shadow-sm'
                                          : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-300 hover:text-emerald-700'
                                      } ${!canEditReview ? 'cursor-not-allowed opacity-50' : ''}`}
                                    >
                                      <ThumbsUp className="h-4 w-4" />
                                      Up
                                    </button>
                                    <button
                                      type="button"
                                      disabled={!canEditReview}
                                      onClick={() => handleVoteChange(slot.key, 'down')}
                                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold transition ${
                                        slotReview.vote === 'down'
                                          ? 'border-rose-500 bg-rose-500 text-white shadow-sm'
                                          : 'border-slate-200 bg-white text-slate-600 hover:border-rose-300 hover:text-rose-700'
                                      } ${!canEditReview ? 'cursor-not-allowed opacity-50' : ''}`}
                                    >
                                      <ThumbsDown className="h-4 w-4" />
                                      Down
                                    </button>
                                  </div>
                                </div>

                                {slotReview.vote === 'down' && (
                                  <div className="rounded-2xl border border-rose-100 bg-rose-50/60 p-4">
                                    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-start">
                                      <div className="space-y-3">
                                        <div>
                                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                            Correct Ingredient / Type
                                          </label>
                                          <CreatableSelect
                                            isClearable
                                            isDisabled={!canEditReview}
                                            placeholder="Choose a correction or pick Custom..."
                                            options={correctionSelectOptions}
                                            value={selectedCorrectionOption}
                                            styles={selectStyles}
                                            createOptionPosition="first"
                                            onChange={(option) => handleCorrectionChange(slot.key, option)}
                                            onCreateOption={(label) => handleCreateOption(slot.key, label)}
                                            noOptionsMessage={({ inputValue }) =>
                                              inputValue
                                                ? 'No match yet. Create it and add it to the shared catalog.'
                                                : 'Type to search or create a new correction.'
                                            }
                                            formatCreateLabel={(inputValue) => (
                                              <div className="flex flex-col py-1">
                                                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-fuchsia-600">
                                                  Create new correction
                                                </span>
                                                <span className="text-sm font-medium text-slate-800">
                                                  {inputValue}
                                                </span>
                                              </div>
                                            )}
                                          />
                                          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                                            <span>
                                              Pick `Custom / Add new correction...` to add a brand-new option from this UI and save it for later.
                                            </span>
                                            {!selectedCorrectionOption && (
                                              <span className="font-medium text-rose-500">
                                                No correction selected yet.
                                              </span>
                                            )}
                                          </div>
                                          {isCustomCorrectionOpen && (
                                            <div className="mt-3 rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-white to-fuchsia-50 px-4 py-4 shadow-sm">
                                              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                                Add Custom Correction
                                              </label>
                                              <div className="grid gap-3 lg:grid-cols-[minmax(0,1.7fr)_auto] lg:items-start">
                                                <input
                                                  type="text"
                                                  value={customCorrectionDraft}
                                                  disabled={!canEditReview}
                                                  onChange={(event) =>
                                                    handleCustomCorrectionDraftChange(slot.key, event.target.value)
                                                  }
                                                  onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                      event.preventDefault()
                                                      handleCustomCorrectionSubmit(slot.key)
                                                    }
                                                  }}
                                                  placeholder="Type the new ingredient / type option here..."
                                                  className={`min-w-0 w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-700 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-fuchsia-400 focus:ring-4 focus:ring-fuchsia-100 ${!canEditReview ? 'cursor-not-allowed opacity-60' : ''}`}
                                                />
                                                <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
                                                  <button
                                                    type="button"
                                                    disabled={!canEditReview || !customCorrectionDraft.trim()}
                                                    onClick={() => handleCustomCorrectionSubmit(slot.key)}
                                                    className={`inline-flex min-w-[148px] items-center justify-center rounded-2xl px-4 py-3 text-sm font-semibold transition ${
                                                      !canEditReview || !customCorrectionDraft.trim()
                                                        ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                                                        : 'bg-fuchsia-600 text-white shadow-sm hover:bg-fuchsia-700'
                                                    }`}
                                                  >
                                                    Add & Select
                                                  </button>
                                                  <button
                                                    type="button"
                                                    disabled={!canEditReview}
                                                    onClick={() =>
                                                      setCustomCorrectionSlots((currentSlots) => ({
                                                        ...currentSlots,
                                                        [slot.key]: false,
                                                      }))
                                                    }
                                                    className={`inline-flex min-w-[120px] items-center justify-center rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 ${!canEditReview ? 'cursor-not-allowed opacity-50' : ''}`}
                                                  >
                                                    Cancel
                                                  </button>
                                                </div>
                                              </div>
                                              <p className="mt-2 text-xs text-slate-500">
                                                Once added, this option is saved to the shared catalog and becomes selectable for future consultations too.
                                              </p>
                                            </div>
                                          )}
                                        </div>

                                        <div>
                                          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                            Why Was It Wrong?
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            {REVIEW_REASON_OPTIONS.map((reason) => {
                                              const isSelected = slotReview.reason_tags.includes(reason.value)
                                              return (
                                                <button
                                                  key={reason.value}
                                                  type="button"
                                                  disabled={!canEditReview}
                                                  onClick={() => handleReasonToggle(slot.key, reason.value)}
                                                  className={`rounded-full px-3 py-2 text-xs font-semibold transition ${
                                                    isSelected
                                                      ? 'bg-slate-900 text-white shadow-sm'
                                                      : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
                                                  } ${!canEditReview ? 'cursor-not-allowed opacity-50' : ''}`}
                                                >
                                                  {reason.label}
                                                </button>
                                              )
                                            })}
                                          </div>
                                        </div>

                                        {isOtherReasonSelected && (
                                          <div>
                                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                              What was wrong?
                                            </label>
                                            <textarea
                                              value={otherReasonDraft}
                                              disabled={!canEditReview}
                                              onChange={(event) =>
                                                handleOtherReasonChange(slot.key, event.target.value)
                                              }
                                              onBlur={() => handleOtherReasonBlur(slot.key)}
                                              rows={3}
                                              placeholder="Add a short note for what the fixed reason tags missed..."
                                              className={`w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-fuchsia-400 focus:ring-4 focus:ring-fuchsia-100 ${!canEditReview ? 'cursor-not-allowed opacity-60' : ''}`}
                                            />
                                            <p className="mt-2 text-xs text-slate-500">
                                              This note autosaves and stays attached to the slot as extra review context.
                                            </p>
                                          </div>
                                        )}
                                      </div>

                                      <div className="rounded-2xl border border-white/70 bg-white/90 px-4 py-3 text-sm text-slate-500 xl:max-w-[220px]">
                                        <div className="mb-2 flex items-center gap-2 text-slate-700">
                                          <Sparkles className="h-4 w-4 text-fuchsia-500" />
                                          <span className="font-semibold">Training Signal</span>
                                        </div>
                                        <p className="leading-6">
                                          Down votes, corrections, and reason tags stay editable and get stored as model-improvement feedback.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </section>
                )
              })}
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <JsonDrawer
                title="Target Output JSON"
                payload={targetOutput.rawText || (targetOutput.parsed ? JSON.stringify(targetOutput.parsed, null, 2) : '')}
              />
              <JsonDrawer
                title="Model Output JSON"
                payload={modelOutput.rawText || (modelOutput.parsed ? JSON.stringify(modelOutput.parsed, null, 2) : '')}
              />
            </div>

            {!canEditReview && (
              <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>You can view the comparison here, but only super admins can edit the review.</span>
              </div>
            )}

            {canEditReview && (
              <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>Autosave is on. You can flip votes, update corrections, and keep editing at any time.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SkinModelReviewModal
